import { ArrowLeft, AlertTriangle, XCircle, Check } from 'lucide-react';
import { useEffect } from 'react';
import { PageShell } from './layout/PageShell';

interface PrivacyPolicyPageProps {
  onBack: () => void;
}

const PRIVACY_POLICY_LAST_UPDATED = '2. mars 2026';

export function PrivacyPolicyPage({ onBack }: PrivacyPolicyPageProps) {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
      {/* Content - scroll handled by main / App wrapper */}
      <PageShell className="pt-page-safe-sm pb-android-pwa-doc xl:max-w-4xl xl:mx-auto xl:px-4 xl:px-8 xl:pt-5 xl:pb-24">
          {/* Content container - fullscreen on mobile, rounded modal on desktop */}
          <div className="pt-5 xl:bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:p-8">
            {/* Back button */}
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 px-4 xl:px-0 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake</span>
            </button>

            {/* Header */}
            <h1 className="text-3xl sm:text-4xl mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 px-4 xl:px-0">
              Personvernerklæring
            </h1>
            <p className="text-gray-600 mb-8 px-4 xl:px-0">
              Sist oppdatert: {PRIVACY_POLICY_LAST_UPDATED}
            </p>

            {/* Content */}
            <div className="prose prose-purple max-w-none space-y-6 px-4 xl:px-0">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Introduksjon</h2>
                <p className="text-gray-700 leading-relaxed">
                  Velkommen til Talentfunn. Vi tar personvernet ditt på alvor og er forpliktet til å beskytte dine personopplysninger i henhold til EUs personvernforordning (GDPR) og norsk personvernlovgivning.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Denne personvernerklæringen forklarer hvilke data vi samler inn, hvordan vi bruker dem, og dine rettigheter knyttet til dine personopplysninger.
                </p>
              </section>

              {/* Who we are */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Hvem vi er</h2>
                <p className="text-gray-700 leading-relaxed">
                  Talentfunn er en plattform for å koble talenter med arbeidsgivere. Tjenesten drives av:
                </p>
                <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100 mt-3">
                  <p className="text-gray-700">
                    <strong>Evensen Solutions</strong>
                  </p>
                  <p className="text-gray-700 mt-1">
                    <strong>Adresse:</strong> Blåknappveien 9, 4823 Nedenes
                  </p>
                  <p className="text-gray-700 mt-1">
                    <strong>Org.nr:</strong> 933 184 048
                  </p>
                  <p className="text-gray-700 mt-2">
                    <strong>E-post:</strong> kontakt@talentfunn.no
                  </p>
                </div>
              </section>

              {/* Data we collect */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Hvilke data vi samler inn</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  For å tilby deg våre tjenester, samler vi inn følgende personopplysninger:
                </p>
                
                <h3 className="text-xl text-purple-800 mb-2 mt-4">Grunnleggende kontoinformasjon</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Brukernavn:</strong> Et unikt brukernavn (3-20 tegn) som du velger ved registrering. Dette er ditt offentlige navn på plattformen og vises til alle brukere.
                  </li>
                  <li>
                    <strong>E-postadresse:</strong> Din e-post brukes for autentisering, kontoadministrasjon, e-postvarsler og viktige servicemeldinger. Du må bekrefte din e-postadresse ved registrering.
                  </li>
                  <li>
                    <strong>Passord:</strong> Ditt passord lagres kryptert (hashet med bcrypt) og kan aldri gjenopprettes i klartekst.
                  </li>
                  <li>
                    <strong>Brukertype:</strong> Om du er registrert som talent eller arbeidsgiver.
                  </li>
                  <li>
                    <strong>Alderbekreftelse:</strong> At du er 15 år eller eldre, og har samtykke fra foresatte hvis du er under 18 år (lagres kun som dato for godkjenning).
                  </li>
                  <li>
                    <strong>Vilkårsaksept:</strong> Dato og versjon av vilkår du aksepterte ved registrering.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Profilinformasjon (valgfritt)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Profilbilde:</strong> Du kan frivillig laste opp et profilbilde (maks 10MB) som lagres i Supabase Storage og vises på din profil.
                  </li>
                  <li>
                    <strong>Avatar-bilde:</strong> Mindre versjon av profilbildet brukt i meldinger og søkeresultater.
                  </li>
                  <li>
                    <strong>Showcase-bilde:</strong> Stort bakgrunnsbilde på profilen din.
                  </li>
                  <li>
                    <strong>Biografitekst:</strong> Personlig beskrivelse av deg selv (maks 1000 tegn).
                  </li>
                  <li>
                    <strong>Lokasjon:</strong> Hvor du befinner deg geografisk.
                  </li>
                  <li>
                    <strong>Ferdigheter:</strong> Liste over dine kompetanser.
                  </li>
                  <li>
                    <strong>Kategorier:</strong> Bransjer/områder du jobber innenfor.
                  </li>
                  <li>
                    <strong>Jobbønsker:</strong> Hva slags jobb du ønsker (kun for talenter).
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Portfolio (kun talenter)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Prosjekttittel og beskrivelse:</strong> Informasjon om dine portfolio-prosjekter.
                  </li>
                  <li>
                    <strong>Prosjektbilder:</strong> Bilder knyttet til hvert prosjekt (maks 10MB per bilde), lagret i Supabase Storage.
                  </li>
                  <li>
                    <strong>Videoer (kun Pluss):</strong> Pluss-brukere kan laste opp én video per prosjekt (maks 500MB), lagret på Bunny.net sine EU-servere. <strong className="text-blue-900">VIKTIG: Alle opplastede videoer er offentlig tilgjengelige og kan ses av alle besøkende på Talentfunn.no uten innlogging.</strong> Du må eksplisitt godta dette ved opplasting.
                  </li>
                  <li>
                    <strong>Rekkefølge:</strong> Sorteringsrekkefølge for prosjektene dine.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Arbeidsgiverinformasjon</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Organisasjonsnummer:</strong> Valideres mot Brønnøysundregistrene.
                  </li>
                  <li>
                    <strong>Fullt juridisk navn:</strong> Navn på personen som registrerer bedriften.
                  </li>
                  <li>
                    <strong>Din rolle:</strong> Stillingstittel i bedriften (f.eks. Daglig leder, HR-ansvarlig).
                  </li>
                  <li>
                    <strong>Verifiseringsstatus:</strong> Om bedriften er godkjent av Talentfunn (kreves for full tilgang).
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Aktivitets- og bruksdata</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Sist aktiv:</strong> Tidspunkt for når du sist var på plattformen (brukes for online-status).
                  </li>
                  <li>
                    <strong>Opprettelsesdato:</strong> Når kontoen din ble opprettet.
                  </li>
                  <li>
                    <strong>Online-status synlighet:</strong> Om du har valgt å skjule online-status.
                  </li>
                  <li>
                    <strong>Søkevisning:</strong> Om profilen din vises i søkeresultater (automatisk skjult etter 6 måneder inaktivitet).
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Meldinger</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Meldingsinnhold:</strong> Tekstmeldinger du sender til andre brukere.
                  </li>
                  <li>
                    <strong>Meldingsbilder:</strong> Bilder du sender i meldinger (maks 10MB), lagret i Supabase Storage.
                  </li>
                  <li>
                    <strong>Tidsstempel:</strong> Når meldinger ble sendt.
                  </li>
                  <li>
                    <strong>Lesestatus:</strong> Om meldinger er lest eller ulest.
                  </li>
                  <li>
                    <strong>Basis:</strong> Om du har betalt for meldingsfunksjonalitet (Arbeidsgiver Basis-abonnement eller enkeltkjøp).
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Abonnements- og betalingsdata</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Abonnementsnivå:</strong> Gratis eller Pluss.
                  </li>
                  <li>
                    <strong>Betalingsreferanse:</strong> Din ID hos betalingsleverandøren (Nets/Nexi), f.eks. abonnement-/betalings-ID brukt for å håndtere abonnement.
                  </li>
                  <li>
                    <strong>Betalingshistorikk:</strong> Lagret hos betalingsleverandøren, ikke på våre servere.
                  </li>
                  <li>
                    <strong>Kundeforhold:</strong> Om du er forbruker eller næringsdrivende (for korrekt håndtering av forbruker- vs. næringskjøp).
                  </li>
                  <li>
                    <strong>Rabattkoder:</strong> Eventuelle rabattkoder du har brukt ved kjøp.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Varsler og preferanser</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>E-postvarsler:</strong> Om du har aktivert e-postvarsler for meldinger.
                  </li>
                  <li>
                    <strong>Push-notifikasjoner:</strong> Hvis aktivert, lagrer vi teknisk informasjon (endpoint URL og krypteringsnøkler) nødvendig for å sende varsler til din enhet. Vi sender kun korte, generiske push-varsler uten hele meldingsinnhold.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Relasjoner til andre brukere</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Favoritter:</strong> Profiler du har lagt til som favoritt.
                  </li>
                  <li>
                    <strong>Blokkerte brukere:</strong> Brukere du har blokkert (de kan ikke sende deg meldinger eller se profilen din).
                  </li>
                  <li>
                    <strong>Rapporter:</strong> Rapporter du har sendt inn om regelbrudd.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Tekniske data</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Autentiseringstokens:</strong> Supabase Auth håndterer JWT-tokens for innlogging.
                  </li>
                  <li>
                    <strong>Service Worker cache:</strong> For offline-funksjonalitet i PWA-appen (lagres kun lokalt i nettleseren).
                  </li>
                  <li>
                    <strong>IP-adresser:</strong> Kun midlertidig lagret av Bunny.net for video-streaming (20-30 sekunder), deretter slettet. I tillegg kan IP-adresser behandles i sikkerhetslogger hos våre tjenester (blant annet Supabase, Netlify og vår egen backend) for å forhindre misbruk og sikre drift; slike logger oppbevares i begrenset tid (f.eks. inntil 30 dager der det gjelder).
                  </li>
                </ul>
              </section>

              {/* How we use data */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Hvordan vi bruker dine data</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi bruker dine personopplysninger til følgende formål:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Tjenesteleveranse:</strong> For å gi deg tilgang til Talentfunn-plattformen og dens funksjoner (profiler, søk, meldinger, portfolio).
                  </li>
                  <li>
                    <strong>Autentisering:</strong> For å sikre at bare du får tilgang til din konto.
                  </li>
                  <li>
                    <strong>Kommunikasjon:</strong> For å gjøre det mulig for deg å sende og motta meldinger, samt motta varsler om aktivitet.
                  </li>
                  <li>
                    <strong>Søk og matching:</strong> For å gjøre profilen din synlig for arbeidsgivere som søker etter talenter med dine ferdigheter, eller talenter for arbeidsgivere.
                  </li>
                  <li>
                    <strong>Smart søkefunksjonalitet:</strong> Vårt 6-lags søkesystem bruker dine ferdigheter og kategorier for å finne relevante matches (ca. 1300 mappings mellom kategorier og ferdigheter).
                  </li>
                  <li>
                    <strong>Betalingsbehandling:</strong> For å håndtere abonnementer og betaling via Nets/Nexi.
                  </li>
                  <li>
                    <strong>Kontoadministrasjon:</strong> For å administrere din konto, abonnement og innstillinger.
                  </li>
                  <li>
                    <strong>Varsler:</strong> For å informere deg om nye meldinger, viktige oppdateringer og systemvarsler (in-app, e-post eller push).
                  </li>
                  <li>
                    <strong>Sikkerhet og moderering:</strong> For å beskytte mot regelbrudd, håndtere rapporter og blokkere brukere ved behov.
                  </li>
                  <li>
                    <strong>Bedriftsverifisering:</strong> For å validere at arbeidsgivere representerer reelle bedrifter.
                  </li>
                  <li>
                    <strong>Inaktivitetshåndtering:</strong> For å automatisk skjule inaktive profiler (6+ måneder) og varsle om kontosletting (24+ måneder).
                  </li>
                </ul>
              </section>

              {/* Legal basis */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Rettslig grunnlag for behandling</h2>
                <p className="text-gray-700 leading-relaxed">
                  Vi behandler dine personopplysninger basert på følgende rettslige grunnlag:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
                  <li>
                    <strong>Samtykke (GDPR Art. 6(1)(a)):</strong> Ved å registrere deg og akseptere vilkårene, gir du ditt eksplisitte samtykke til behandling av dine personopplysninger som beskrevet i denne erklæringen. Du kan når som helst trekke tilbake samtykket ved å slette kontoen din.
                  </li>
                  <li>
                    <strong>Kontraktoppfyllelse (GDPR Art. 6(1)(b)):</strong> Behandlingen er nødvendig for å oppfylle avtalen om bruk av tjenesten (levere profil, meldinger, søkefunksjon).
                  </li>
                  <li>
                    <strong>Berettiget interesse (GDPR Art. 6(1)(f)):</strong> Vi har en berettiget interesse i å forbedre og administrere tjenesten vår, sikre trygghet og forhindre misbruk.
                  </li>
                  <li>
                    <strong>Rettslig forpliktelse (GDPR Art. 6(1)(c)):</strong> For å overholde juridiske krav som bokføringsloven (lagring av betalingsdata i 5 år).
                  </li>
                </ul>
              </section>

              {/* Third party processors */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Tredjepartsbehandlere</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  For å levere våre tjenester, benytter vi følgende tredjepartstjenester som behandler personopplysninger på våre vegne. Alle har inngått databehandleravtaler (DPA) med oss:
                </p>
                <div className="space-y-4">
                  <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100">
                    <h3 className="font-semibold text-purple-900 mb-2">Supabase</h3>
                    <p className="text-gray-700 text-sm">
                      <strong>Formål:</strong> Database (PostgreSQL), autentisering (Supabase Auth), fillagring (Supabase Storage), backend API (Edge Functions).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Data som deles:</strong> All brukergenerert data (profiler, meldinger, bilder, portfolio, favoritter, blokkerte brukere).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Lokasjon:</strong> EU/EØS-datasentre (primært Frankfurt, Tyskland).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Personvern:</strong> Supabase er GDPR-kompatibel og følger strenge sikkerhetsstandarder. Data Processing Agreement (DPA) er på plass.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Mer info:</strong> <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">supabase.com/privacy</a>
                    </p>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100">
                    <h3 className="font-semibold text-purple-900 mb-2">Nets / Nexi</h3>
                    <p className="text-gray-700 text-sm">
                      <strong>Formål:</strong> Behandling av betalinger for Pluss-abonnement og enkeltkjøp av meldingstilgang.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Data som deles:</strong> E-post, navn (hvis oppgitt), og betalingsreferanser (f.eks. abonnement-/betalings-ID). Nets/Nexi håndterer kortinformasjon direkte - vi får aldri tilgang til kortdetaljer.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Sikkerhet:</strong> Nets/Nexi håndterer kortdata og er PCI DSS-sertifisert betalingsleverandør.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Personvern:</strong> Nets/Nexi er GDPR-kompatibel og tilbyr databehandleravtale (DPA).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Checkout-funksjon:</strong> Nexi Checkout håndterer betalingssiden - ingen kortdata går gjennom våre servere.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Mer info:</strong> <a href="https://www.nexigroup.com/en/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Nexi Group personvern</a>
                    </p>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100">
                    <h3 className="font-semibold text-purple-900 mb-2">Mailgun</h3>
                    <p className="text-gray-700 text-sm">
                      <strong>Formål:</strong> Utsending av transaksjonelle e-poster (e-postverifisering, passordtilbakestilling, inaktivitetsvarsler og viktige servicemeldinger).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Data som deles:</strong> E-postadresse, navn/brukernavn der relevant, samt nødvendig e-postinnhold.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Lokasjon:</strong> EU-region når Mailgun-region er konfigurert til <code>eu</code>.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Personvern:</strong> Mailgun brukes som databehandler med databehandleravtale (DPA).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Mer info:</strong> <a href="https://www.mailgun.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">mailgun.com/legal/privacy-policy</a>
                    </p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-2">Bunny.net (Video Streaming)</h3>
                    <p className="text-gray-700 text-sm">
                      <strong>Formål:</strong> Lagring og streaming av brukeropplastede videoer i portfolioprosjekter (kun Pluss-brukere).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Data som deles:</strong> Videoinnhold, metadata (filnavn, størrelse), IP-adresser for streaming (lagres i 20-30 sekunder, deretter slettet).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Lokasjon:</strong> Kun EU-servere (Frankfurt/Stockholm). All data flyter utelukkende gjennom europeiske datasentre - ingen overføring til tredjeland.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Personvern:</strong> Bunny.net er GDPR-compliant og opererer som databehandler i henhold til GDPR Art. 28. Vi har Data Processing Agreement (DPA) med Bunny.net.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Sletting:</strong> Når du sletter en video i Talentfunn, sender vi sletteforespørsel til Bunny.net umiddelbart. Endelig fjerning i underliggende infrastruktur kan variere.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Sikkerhet:</strong> Videoer lagres i tilgangsstyrte biblioteker hos Bunny.net. Selv om videoene er offentlig tilgjengelige på Talentfunn.no, håndteres lagring og streaming sikkert på Bunny.net sine servere. Streaming skjer via kryptert HTTPS.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Mer info:</strong> <a href="https://bunny.net/gdpr/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">bunny.net/gdpr</a>
                    </p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
                    <h3 className="font-semibold text-green-900 mb-2">Netlify</h3>
                    <p className="text-gray-700 text-sm">
                      <strong>Formål:</strong> Hosting av web-applikasjonen (frontend).
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Data som deles:</strong> Netlify håndterer primært statiske filer (HTML, CSS, JavaScript). Netlify kan imidlertid behandle IP-adresser og HTTP-headers i sikkerhets- og tilgjengelighetslogger. Disse personopplysningene lagres ikke permanent og slettes automatisk etter 30 dager.
                    </p>
                    <p className="text-gray-700 text-sm mt-1">
                      <strong>Mer info:</strong> <a href="https://www.netlify.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">netlify.com/privacy</a>
                    </p>
                  </div>
                </div>
              </section>

              {/* Data retention */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Lagringstid</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi lagrer dine personopplysninger så lenge du har en aktiv konto hos oss. Ved kontosletting gjelder følgende:
                </p>
                
                <h3 className="text-xl text-purple-800 mb-2 mt-4">Aktive kontoer</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Data lagres så lenge kontoen er aktiv og du bruker tjenesten.</li>
                  <li>Du kan når som helst slette kontoen din og all tilhørende data.</li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Inaktive kontoer (automatisk håndtering)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>6 måneder inaktivitet:</strong> Profilen din skjules automatisk fra søkeresultater, men kontoen forblir aktiv. Du kan reaktivere søkevisning ved å logge inn.
                  </li>
                  <li>
                    <strong>24 måneder inaktivitet:</strong> Vi sender deg en e-postvarsel om at kontoen vil slettes om 30 dager hvis du ikke logger inn. Dette er i tråd med GDPR-prinsippet om dataminimering.
                  </li>
                  <li>
                    <strong>Etter 30 dager:</strong> Hvis du ikke har logget inn, slettes kontoen automatisk og permanent.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Ved kontosletting (manuell eller automatisk)</h3>
                <div className="bg-red-50 p-4 rounded-xl border-2 border-red-200 mt-2">
                  <p className="text-gray-700 font-medium mb-2">Følgende slettes UMIDDELBART:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 text-sm">
                    <li>Din profil og all profilinformasjon (brukernavn, bio, lokasjon, ferdigheter, jobbønsker)</li>
                    <li>Alle profilbilder (profil, avatar, showcase) fra Supabase Storage</li>
                    <li>Alle portfolioprosjekter og tilhørende bilder</li>
                    <li>Alle videoer (slettes fra Bunny.net innen 24 timer)</li>
                    <li>Alle meldinger du har sendt og mottatt</li>
                    <li>Alle meldingsbilder fra Supabase Storage</li>
                    <li>Din e-postadresse og autentiseringsinformasjon</li>
                    <li>Favoritter og blokkerte brukere</li>
                    <li>Rapporter du har sendt inn</li>
                    <li>Push-notifikasjon abonnementer</li>
                    <li>Varslingsinnstillinger</li>
                  </ul>
                </div>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Unntaksvis oppbevaring (juridiske krav)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Betalingsdata:</strong> I henhold til bokføringsloven må vi oppbevare betalingsrelatert informasjon (abonnementshistorikk, fakturaer) hos betalingsleverandøren i 5 år etter siste transaksjon.
                  </li>
                  <li>
                    Ingen annen data oppbevares etter kontosletting med mindre det foreligger en rettslig forpliktelse. Organisasjonsnummer og øvrig arbeidsgiverinformasjon slettes ved kontosletting.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Meldinger til andre brukere</h3>
                <p className="text-gray-700 leading-relaxed">
                  Meldinger du har sendt til andre brukere forblir synlige for mottakerne etter at du sletter kontoen din, men avsendernavnet endres til "Slettet bruker" og all personidentifiserende metadata anonymiseres, slik at meldingen ikke lenger kan knyttes til deg. Dette er nødvendig for at mottakernes meldingshistorikk skal forbli intakt.
                </p>
              </section>

              {/* Your rights */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Dine rettigheter (GDPR)</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  I henhold til GDPR har du følgende rettigheter:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Rett til innsyn (Art. 15):</strong> Du har rett til å få en kopi av alle personopplysninger vi har om deg. Gå til <strong>Innstillinger → Mine data → Last ned mine data</strong> for å få en fullstendig kopi av all din data i JSON-format.
                  </li>
                  <li>
                    <strong>Rett til retting (Art. 16):</strong> Du kan redigere og oppdatere din profilinformasjon når som helst gjennom din profil og innstillinger.
                  </li>
                  <li>
                    <strong>Rett til sletting / retten til å bli glemt (Art. 17):</strong> Du kan når som helst slette din konto og all tilhørende data. Gå til <strong>Innstillinger → Slett kontoen min</strong>.
                  </li>
                  <li>
                    <strong>Rett til dataportabilitet (Art. 20):</strong> Du har rett til å motta dine personopplysninger i et strukturert, maskinlesbart format (JSON). Bruk <strong>"Last ned mine data"</strong> i innstillingene.
                  </li>
                  <li>
                    <strong>Rett til begrensning av behandling (Art. 18):</strong> Du kan be om at vi midlertidig begrenser behandlingen av dine data. Kontakt oss på kontakt@talentfunn.no.
                  </li>
                  <li>
                    <strong>Rett til innsigelse (Art. 21):</strong> Du kan protestere mot behandling basert på berettiget interesse. Kontakt oss for å diskutere alternativer.
                  </li>
                  <li>
                    <strong>Rett til å trekke tilbake samtykke (Art. 7(3)):</strong> Du kan når som helst trekke tilbake ditt samtykke til behandling av personopplysninger ved å slette kontoen din.
                  </li>
                  <li>
                    <strong>Rett til å klage (Art. 77):</strong> Du har rett til å klage til Datatilsynet hvis du mener vi behandler dine personopplysninger i strid med GDPR.
                  </li>
                </ul>
              </section>

              {/* Data export */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Eksport av personopplysninger</h2>
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-2xl border-2 border-blue-200">
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Du kan når som helst laste ned en fullstendig kopi av alle dine personopplysninger.</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    For å eksportere dine data:
                  </p>
                  <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                    <li>Klikk på "Innstillinger" i footeren (nederst på siden)</li>
                    <li>Finn seksjonen "Mine data"</li>
                    <li>Klikk på "Last ned mine data (JSON)"</li>
                  </ol>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Du vil motta en JSON-fil som inneholder:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-2">
                    <li><strong>Komplett profilinformasjon:</strong> brukernavn, e-post, bio, lokasjon, fylke, ferdigheter, kategorier, jobbønsker, brukertype</li>
                    <li><strong>Bilder:</strong> profilbilde, showcase-bilde, avatar med fulle URL-er</li>
                    <li><strong>Abonnement:</strong> abonnementsnivå, meldingstilgang, betalingsreferanser, kjøpsdato</li>
                    <li><strong>Datoer:</strong> opprettet, sist aktiv, vilkårsaksept</li>
                    <li><strong>Innstillinger:</strong> skjul online-status, e-postvarsler, søkevisning</li>
                    <li><strong>Vilkår:</strong> vilkårsversjon, akseptdato, fullstendig historikk over alle godkjente vilkår</li>
                    <li><strong>Arbeidsgiverinformasjon:</strong> bedriftsnavn, org.nr, rolle, verifiseringsstatus (hvis relevant)</li>
                    <li><strong>Alle portfolioprosjekter:</strong> titler, beskrivelser, bilder, videoer (med URL-er), rekkefølge</li>
                    <li><strong>Alle meldinger:</strong> sendt og mottatt med tidsstempler, innhold, bilder, lesestatus</li>
                    <li><strong>Favoritter:</strong> alle profiler du har lagt til som favoritt</li>
                    <li><strong>Blokkerte brukere:</strong> alle brukere du har blokkert</li>
                    <li><strong>Rapporter:</strong> alle rapporter du har sendt inn</li>
                    <li><strong>Push-notifikasjoner:</strong> teknisk informasjon om push-abonnementer (endpoint, nøkler)</li>
                    <li><strong>Metadata:</strong> eksportdato, format, versjon, datakategorier</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    JSON-formatet er strukturert og maskinlesbart i henhold til GDPR Art. 20 (dataportabilitet), slik at du enkelt kan importere dataene til andre tjenester eller analysere dem selv.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    <strong>GDPR-garanti:</strong> Eksporten inneholder ALL din data lagret i Talentfunn, uten filtrering, i samsvar med GDPR Art. 15 (rett til innsyn).
                  </p>
                </div>
              </section>

              {/* Data deletion */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Sletting av data</h2>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-200">
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Du har rett til å slette all din data når som helst.</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    For å slette din konto og alle tilhørende data:
                  </p>
                  <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                    <li>Klikk på "Innstillinger" i footeren (nederst på siden)</li>
                    <li>Scroll ned til "Slett kontoen min"</li>
                    <li>Klikk på "Slett kontoen min"</li>
                    <li>Bekreft slettingen</li>
                  </ol>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Når du sletter kontoen din, vil følgende bli permanent fjernet:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-2">
                    <li>Din profil og all profilinformasjon</li>
                    <li>Alle dine portfolio-prosjekter</li>
                    <li>Alle bilder du har lastet opp (slettet fra Supabase Storage)</li>
                    <li>Alle videoer du har lastet opp (slettet fra Bunny.net innen 24 timer)</li>
                    <li>Alle dine meldinger (sendt og mottatt)</li>
                    <li>Din e-postadresse og autentiseringsinformasjon</li>
                    <li>All abonnementsdata (fremtidige faktureringer stoppes automatisk)</li>
                    <li>Favoritter og blokkerte brukere</li>
                    <li>Push-notifikasjon abonnementer</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4 font-medium text-red-600 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
                    Denne handlingen kan ikke angres. All data slettes permanent og kan ikke gjenopprettes.
                  </p>
                </div>
              </section>

              {/* Data security */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Datasikkerhet</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi tar datasikkerhet på alvor og implementerer følgende sikkerhetstiltak:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Kryptert overføring:</strong> All dataoverføring skjer via kryptert HTTPS/TLS 1.3-tilkobling.
                  </li>
                  <li>
                    <strong>Passordkryptering:</strong> Passord lagres kryptert med bcrypt-hashing (aldri i klartekst).
                  </li>
                  <li>
                    <strong>Strenge passordkrav:</strong> Minimum 8 tegn, stor/liten bokstav, tall og spesialtegn.
                  </li>
                  <li>
                    <strong>Autentiseringstokens:</strong> JWT-tokens med kort levetid og sikker signering.
                  </li>
                  <li>
                    <strong>Tilgangskontroll:</strong> Streng tilgangskontroll til backend-systemer og database (Row Level Security i Supabase).
                  </li>
                  <li>
                    <strong>Fillagring:</strong> Private bøtter i Supabase Storage med signerte URL-er (utløper etter 1 time).
                  </li>
                  <li>
                    <strong>Video-sikkerhet:</strong> Bunny.net private biblioteker med tilgangskontroll og kryptert streaming.
                  </li>
                  <li>
                    <strong>Database-backup:</strong> Automatisk backup av data for å forhindre tap (Supabase Point-in-Time Recovery).
                  </li>
                  <li>
                    <strong>Sikkerhetsoppdateringer:</strong> Regelmessige oppdateringer av alle avhengigheter og biblioteker.
                  </li>
                  <li>
                    <strong>E-postverifisering:</strong> Obligatorisk e-postbekreftelse ved registrering.
                  </li>
                  <li>
                    <strong>Moderering:</strong> Rapport- og blokkeringsfunksjoner for å håndtere upassende innhold.
                  </li>
                </ul>
              </section>

              {/* Notifications and communication */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Varsler og kommunikasjon</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn tilbyr ulike måter å holde deg oppdatert på:
                </p>
                
                <h3 className="text-xl text-purple-800 mb-2 mt-4">In-app varsler</h3>
                <p className="text-gray-700 leading-relaxed">
                  Varsler som vises når du er innlogget på plattformen (f.eks. nye meldinger, systemvarsler). Disse er alltid aktivert som standard funksjonalitet og krever ingen samtykke da de kun vises når du aktivt bruker tjenesten.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">E-postvarsler</h3>
                <p className="text-gray-700 leading-relaxed">
                  Viktige oppdateringer sendt til din registrerte e-postadresse (f.eks. nye meldinger, inaktivitetsvarsler, sikkerhetsoppdateringer). Du kan aktivere eller deaktivere dette i <strong>Innstillinger → Varslingsinnstillinger</strong>.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Push-notifikasjoner (PWA)</h3>
                <p className="text-gray-700 leading-relaxed">
                  Varsler som vises på din enhet selv når du ikke har appen åpen. Krever din eksplisitte tillatelse i nettleseren og kan aktiveres/deaktiveres i <strong>Innstillinger → Varslingsinnstillinger</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mt-2">
                  <strong>Teknisk informasjon:</strong> Når du aktiverer push-notifikasjoner, lagrer vi et push-abonnement (endpoint URL og krypteringsnøkler) som trengs for å sende varsler til din enhet. Levering av push-varsler går via nettleserleverandøren på enheten din (for eksempel Apple, Google eller Mozilla). Vi sender ikke hele meldingsinnhold i push-varsler. Du kan når som helst trekke tilbake tillatelsen i nettleserens innstillinger.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Viktige servicemeldinger</h3>
                <p className="text-gray-700 leading-relaxed">
                  Visse e-poster er nødvendige for å levere tjenesten og kan ikke deaktiveres:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-2">
                  <li>E-postverifisering ved registrering</li>
                  <li>Passordtilbakestilling</li>
                  <li>Betalingsbekreftelser fra Nets/Nexi</li>
                  <li>Varsel om kontosletting (24 måneders inaktivitet)</li>
                  <li>Viktige sikkerhetsoppdateringer</li>
                </ul>
              </section>

              {/* Cookies */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Informasjonskapsler (Cookies)</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn bruker nødvendige informasjonskapsler for autentisering og sesjonshåndtering. Disse er essensielle for at tjenesten skal fungere og krever ikke samtykke i henhold til ePrivacy-direktivet.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  I tillegg til cookies bruker vi lokal lagring i nettleseren (<code>localStorage</code> og <code>sessionStorage</code>) for sikker innlogging, drift av app-funksjoner (PWA) og brukerpreferanser. Dette inkluderer blant annet autentiseringsrelatert lagring (for eksempel <code>talentfunn-auth</code>), samtykkestatus for cookie-banner (<code>cookieConsent</code>) og midlertidige sesjonsdata for navigasjon og visning.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Slike lagringsmekanismer brukes kun for teknisk funksjonalitet, sikkerhet og stabil drift av tjenesten, og ikke til annonsering eller sporing på tvers av andre tjenester.
                </p>
                
                <h3 className="text-xl text-purple-800 mb-2 mt-4">Nødvendige cookies (alltid aktive)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Supabase Auth tokens:</strong> JWT-tokens for autentisering og sesjonshåndtering. Utløper automatisk etter 1 time (med refresh-token for fornyelse).
                  </li>
                  <li>
                    <strong>Service Worker cache:</strong> Lagrer statiske ressurser lokalt i nettleseren for offline-funksjonalitet (PWA). Kan slettes i nettleserens innstillinger.
                  </li>
                  <li>
                    <strong>sidebar_state:</strong> En nødvendig cookie som lagrer om sidepanelet er åpent/lukket for bedre brukeropplevelse.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Lokal lagring i nettleser (ikke-cookies)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Autentisering og sesjon:</strong> Nødvendig lagring for å holde deg innlogget og håndtere sikker sesjonsfornyelse.
                  </li>
                  <li>
                    <strong>Innstillinger og preferanser:</strong> For eksempel cookie-bannerstatus, PWA-visning og enkelte UI-valg.
                  </li>
                  <li>
                    <strong>Midlertidige sesjonsnøkler:</strong> Benyttes for navigasjon/scroll-tilstand og slettes normalt ved ny sesjon.
                  </li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">Hva vi IKKE bruker</h3>
                <ul className="list-none pl-0 space-y-1 text-gray-700">
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" aria-hidden />
                    Ingen sporings-cookies fra tredjeparter (Google Analytics, Facebook Pixel, osv.)
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" aria-hidden />
                    Ingen annonsecookies eller markedsføringsteknikker
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" aria-hidden />
                    Ingen fingerprinting eller kryssnettstedssporing
                  </li>
                </ul>
              </section>

              {/* Children's privacy */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Barn og personvern</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn er designet for brukere som er <strong>15 år eller eldre</strong>. Vi har valgt en aldersgrense på 15 år, som er strengere enn lovens minimum (13 år i Norge).
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>15-17 år:</strong> Brukere i denne aldersgruppen må bekrefte at de har samtykke fra foresatte/verge ved registrering.
                  </li>
                  <li>
                    <strong>Under 15 år:</strong> Vi samler ikke bevisst inn personopplysninger fra barn under 15 år. Hvis vi oppdager at et barn under 15 år har opprettet en konto, vil vi umiddelbart slette kontoen.
                  </li>
                  <li>
                    <strong>Foreldre/verger:</strong> Hvis du er forelder eller verge og oppdager at barnet ditt har opprettet en konto uten din viten, vennligst kontakt oss på kontakt@talentfunn.no slik at vi kan slette kontoen.
                  </li>
                </ul>
              </section>

              {/* International transfers */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Internasjonale dataoverføringer</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi prioriterer å holde all data innenfor EU/EØS:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Supabase:</strong> Primært Frankfurt, Tyskland (EU). All data lagres på EU/EØS-servere.
                  </li>
                  <li>
                    <strong>Bunny.net:</strong> KUN EU-servere (Frankfurt/Stockholm). Ingen overføring til tredjeland.
                  </li>
                  <li>
                    <strong>Nets/Nexi:</strong> Europeisk infrastruktur. Eventuelle overforinger utenfor EØS skjer iht. gjeldende overforingsgrunnlag (f.eks. SCC) i leverandorens dokumentasjon.
                  </li>
                  <li>
                    <strong>Mailgun:</strong> Brukes med EU-region (<code>eu</code>) for e-postlevering. Eventuelle overføringer utenfor EU/EØS skjer kun med gyldig overføringsgrunnlag (f.eks. SCC).
                  </li>
                  <li>
                    <strong>Netlify:</strong> Kan bruke globale servere for CDN, men lagrer ingen personopplysninger (kun statiske filer).
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Alle overføringer til tredjeland (utenfor EU/EØS) skjer kun når nødvendig og med passende beskyttelsestiltak i henhold til GDPR Kapittel V (Standard Contractual Clauses).
                </p>
              </section>

              {/* Changes to policy */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Endringer i personvernerklæringen</h2>
                <p className="text-gray-700 leading-relaxed">
                  Vi kan oppdatere denne personvernerklæringen fra tid til annen for å reflektere endringer i våre tjenester, juridiske krav eller beste praksis for personvern.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Ved vesentlige endringer:</strong> Vi vil varsle deg via e-post eller gjennom en merkbar melding på plattformen minst 30 dager før endringene trer i kraft. Du vil ha mulighet til å vurdere endringene og trekke tilbake samtykket ditt ved å slette kontoen din hvis du ikke aksepterer de nye vilkårene.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Ved mindre endringer:</strong> (f.eks. rettskrivingsfeil, presiseringer) vil vi oppdatere datoen øverst i dokumentet.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Vi anbefaler at du leser gjennom personvernerklæringen regelmessig for å holde deg oppdatert.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Kontakt oss</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Hvis du har spørsmål om denne personvernerklæringen, ønsker å utøve dine rettigheter, eller har bekymringer om hvordan vi håndterer dine personopplysninger, kan du kontakte oss. Vi har ikke personvernombud, men alle personvernhenvendelser håndteres av oss:
                </p>
                <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100">
                  <p className="text-gray-700">
                    <strong>Evensen Solutions</strong>
                  </p>
                  <p className="text-gray-700 mt-1">
                    <strong>Org.nr:</strong> 933 184 048
                  </p>
                  <p className="text-gray-700 mt-2">
                    <strong>E-post (personvern):</strong> kontakt@talentfunn.no
                  </p>
                  <p className="text-gray-700 mt-2">
                    Vi svarer så fort vi kan, normalt innen 72 timer. For GDPR-forespørsler (innsyn, sletting, osv.) vil vi svare innen 30 dager som påkrevd av GDPR Art. 12.
                  </p>
                </div>
              </section>

              {/* Data Protection Authority */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Datatilsynet</h2>
                <p className="text-gray-700 leading-relaxed">
                  Hvis du mener at vi ikke overholder personvernregelverket, har du rett til å klage til Datatilsynet i henhold til GDPR Art. 77:
                </p>
                <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100 mt-3">
                  <p className="text-gray-700">
                    <strong>Datatilsynet</strong>
                  </p>
                  <p className="text-gray-700 mt-1">
                    Postboks 458 Sentrum<br />
                    0105 Oslo<br />
                    Telefon: 22 39 69 00<br />
                    E-post: postkasse@datatilsynet.no<br />
                    Nettside: <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">www.datatilsynet.no</a>
                  </p>
                </div>
              </section>

              {/* Summary */}
              <section className="border-t-2 border-purple-200 pt-6 mt-8">
                <h2 className="text-2xl text-purple-900 mb-3">Oppsummering</h2>
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-2xl">
                  <ul className="space-y-2 text-gray-800">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Vi samler kun inn data som er nødvendig for å levere tjenesten</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Du eier dine data og kan slette dem når som helst (retten til å bli glemt)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Vi bruker kun GDPR-kompatible tredjepartstjenester (Supabase, Nets/Nexi, Bunny.net, Mailgun)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>All data lagres sikkert med moderne krypteringsmetoder (HTTPS, bcrypt, JWT)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Du har full kontroll over hvilken informasjon som vises i profilen din</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Du kan når som helst eksportere all din data i JSON-format</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Vi bruker ingen sporings- eller annonsecookies fra tredjeparter</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 flex-shrink-0 mt-1 text-purple-600" aria-hidden />
                      <span>Inaktive kontoer slettes automatisk etter 24 måneder (med 30 dagers varsel)</span>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
      </PageShell>
    </div>
  );
}
